/**
 * cycleViewState (#2408) — the ``?cc=`` deep-link state machine for the
 * Ernteprognose page.
 *
 * Links from other surfaces (farm twin, Deckungsbeitrag, chat) target crop_cycle
 * ids, not lens groups — including next season's register rows (``crop_year >
 * currentWj``). v2 resolved every cc to the plain crop view, so a future
 * planning cycle landed on a false "Noch keine Ernte" dead end. This pure
 * module classifies the cc into a discriminated view state the page renders as
 * ONE banner surface (#2407's overlay offer + the Planung banner + the
 * prior-year framing are branches of the same surface, never parallel banners):
 *
 *   - ``default``       — absent/unknown cc, or a cc outside every lens group
 *                         (e.g. a blocklisted Brache cycle): default group view.
 *   - ``current``       — cc in the current SEASON (or without a usable
 *                         crop_year): plain crop view.
 *   - ``past``          — past cc, crop has current-season members: plain crop view.
 *   - ``priorFraming``  — no current-season members but the crop HAS history: the
 *                         honest season-scoped neutral banner + #2407's overlay offer.
 *   - ``planung``       — ANY future-crop_year cc: "Saison … — Planung, noch
 *                         nicht begonnen", with a one-click switch to a
 *                         deterministic current-season member when one exists,
 *                         else prior-framing when the crop has history, else bare.
 *
 * #2610 — "current" is a SEASON (``crop_year``), resolved by
 * ``resolveCurrentSeason``, NOT the wall-clock Wirtschaftsjahr. That distinction
 * is the whole pre-rollover half of the bug: an early crop harvesting between
 * January and ``wj_start_month`` carries a ``crop_year`` one ahead of the
 * wall-clock WJ, so grading it against the WJ classified an ACTIVELY HARVESTING
 * season as "Planung, noch nicht begonnen". The classification rule below is
 * unchanged — it is fed the season now, which is what makes it correct.
 *
 * Pure + unit-tested; no React/recharts/network.
 */
import type { OutputCycleInfo } from '../../../hooks/useErnteprognose';
import type { OutputVerdict } from './outputSeries';
import { cropYearNumOf, firstCycleOfGroup, type CropLensGroup } from './lensGroups';

/** Discriminated ``?cc=`` view state — see the module docblock for the map. */
export type CycleViewState =
  | { kind: 'default' }
  | { kind: 'current' }
  | { kind: 'past' }
  | { kind: 'priorFraming' }
  | {
      kind: 'planung';
      /** The planning cycle's crop_year (harvest season) — the Saison in the banner. */
      cropYear: number;
      /** Deterministic current-season member to switch to; null when none exists. */
      switchCc: string | null;
      /** True when the crop has history but no current members — render the overlay offer. */
      priorFraming: boolean;
    };

/**
 * Classify the ``?cc=`` deep link. ``groups`` must be ALL lens groups (never a
 * display-filtered list — a cc into a collapsed zero-data group still resolves,
 * #2407 Gap 3). No cc state dead-ends: every branch renders the crop view plus
 * at most one banner.
 */
export function resolveCycleView(
  ccParam: string | null,
  cycles: OutputCycleInfo[],
  groups: CropLensGroup[],
  currentSeason: number,
): CycleViewState {
  if (!ccParam) return { kind: 'default' };
  const cycle = cycles.find((c) => c.cropCycleId === ccParam);
  if (!cycle) return { kind: 'default' };
  const group = groups.find((g) => g.memberIds.includes(ccParam));
  if (!group) return { kind: 'default' };

  const year = cropYearNumOf(cycle);
  // Null-cropYear cycles are members but excluded from current/future detection
  // (#2408) — they get the plain crop view, never a Saison banner.
  if (year == null) return { kind: 'current' };

  const hasCurrent = group.currentWjCount > 0;
  const hasHistory = group.members.some((m) => {
    const y = cropYearNumOf(m);
    return y != null && y < currentSeason;
  });

  if (year > currentSeason) {
    return {
      kind: 'planung',
      cropYear: year,
      // ``firstCycleOfGroup`` ranks current-season members first with an id
      // tiebreak, so with ``hasCurrent`` it IS the deterministic current member.
      switchCc: hasCurrent ? firstCycleOfGroup(group, currentSeason) : null,
      priorFraming: !hasCurrent && hasHistory,
    };
  }
  if (year === currentSeason) return { kind: 'current' };
  return !hasCurrent && hasHistory ? { kind: 'priorFraming' } : { kind: 'past' };
}

/** Planung banner copy (#2408) — the Ampel word + the Saison sentence. */
export const PLANUNG_HEADLINE = 'Planung';
export const planungSentence = (cropYear: number): string =>
  `Saison ${cropYear} — Planung, noch nicht begonnen.`;

/**
 * Honest neutral copy for a crop WITH harvest history (#2408): the stock
 * "Noch keine Ernte — für diese Kultur ist noch keine Ernte erfasst." claims
 * the CROP never harvested, which is false whenever prior-year members exist.
 * The replacement scopes the claim to the current SEASON (#2610: it used to say
 * "im laufenden Wirtschaftsjahr", which was both the wrong frame and — for an
 * early crop whose harvest ended before the WJ started — simply untrue).
 */
export const HISTORY_NEUTRAL_HEADLINE = 'Keine Ernte in der laufenden Saison';
export const HISTORY_NEUTRAL_SENTENCE =
  'In der laufenden Saison ist bisher keine Ernte erfasst — für diese Kultur liegen Vorjahresdaten vor.';

/**
 * The verdict the ONE banner surface actually shows. Planung overrides ANY
 * computed verdict (a planning deep link explains itself before it grades
 * progress); the no-actuals neutral verdict is re-copied when the crop has
 * history ("Noch keine Ernte" must never render for a crop with harvest
 * history, #2408). Everything else — including "Kein Vergleich", whose
 * ``actualToDate`` is non-null — passes through untouched (#2407 verbatim).
 */
export function resolveBannerVerdict(
  verdict: OutputVerdict,
  state: CycleViewState,
  cropHasHistory: boolean,
): OutputVerdict {
  if (state.kind === 'planung') {
    return {
      level: 'neutral',
      headline: PLANUNG_HEADLINE,
      sentence: planungSentence(state.cropYear),
      actualToDate: null,
      forecastToDate: null,
      ratio: null,
      atBucket: null,
    };
  }
  if (verdict.level === 'neutral' && verdict.actualToDate == null && cropHasHistory) {
    return {
      level: 'neutral',
      headline: HISTORY_NEUTRAL_HEADLINE,
      sentence: HISTORY_NEUTRAL_SENTENCE,
      actualToDate: null,
      forecastToDate: null,
      ratio: null,
      atBucket: null,
    };
  }
  return verdict;
}
